import { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatabaseBackup, Download, Trash2, Undo2, AlertTriangle, Mail, Clock } from "lucide-react";

const FREQS = [
  ["30min", "Every 30 Minutes"], ["hourly", "Hourly"], ["daily", "Daily"],
  ["weekly", "Weekly"], ["monthly", "Monthly"],
];

const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(2)} MB` : `${(n / 1024).toFixed(1)} KB`);
const fmtDT = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });

function PageHeader({ eyebrow, title, sub, testId, children }) {
  return (
    <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
      <div>
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">{eyebrow}</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter" data-testid={testId}>{title}</h1>
        <div className="text-xs text-muted-foreground mt-2">{sub}</div>
      </div>
      {children}
    </div>
  );
}

export function OrgBackupPage() {
  const [backups, setBackups] = useState([]);
  const [st, setSt] = useState({ frequency: "30min", auto_email: false });
  const [emailText, setEmailText] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/backups").then(r => setBackups(r.data)).catch(e => toast.error(formatErr(e)));
    api.get("/backup/settings").then(r => {
      setSt({ frequency: r.data.frequency, auto_email: r.data.auto_email });
      setEmailText((r.data.emails || []).join(", "));
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const createBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/backups");
      toast.success(`Backup created — ${data.filename} (${fmtSize(data.size)}, ${data.record_count} records)`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const download = async (b) => {
    try {
      const r = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = b.filename; a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const del = async (b) => {
    if (!confirm(`Delete backup "${b.filename}"?`)) return;
    try { await api.delete(`/backups/${b.id}`); toast.success("Backup deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const parsedEmails = () => emailText.split(/[,\n;]+/).map(e => e.trim()).filter(Boolean);

  const saveSettings = async (silent = false) => {
    const emails = parsedEmails();
    const bad = emails.find(e => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
    if (bad) { toast.error(`Invalid email address: ${bad}`); return false; }
    setSaving(true);
    try {
      await api.put("/backup/settings", { emails, frequency: st.frequency, auto_email: st.auto_email });
      if (!silent) toast.success("Backup settings saved");
      return true;
    } catch (e) { toast.error(formatErr(e)); return false; }
    finally { setSaving(false); }
  };

  const sendNow = async () => {
    if (!(await saveSettings(true))) return;
    if (!parsedEmails().length) { toast.error("Enter at least one email address"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/backups/send-now");
      toast.success(`Backup "${data.backup}" emailed to ${data.sent_to.join(", ")}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="org-backup-page">
      <PageHeader eyebrow="Settings · Backup & Restore" title="Backup" testId="org-backup-title"
        sub="Full organisation data — transactions, master records, stock, users and settings">
        <Button data-testid="ob-create-btn" onClick={createBackup} disabled={busy}
          className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
          <DatabaseBackup className="w-4 h-4 mr-1"/> {busy ? "Creating…" : "Create Backup"}
        </Button>
      </PageHeader>

      <div className="border border-border rounded-sm overflow-hidden mb-6" data-testid="ob-list">
        <table className="w-full text-sm" data-testid="ob-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Backup Name</th>
              <th className="text-left p-3">Date &amp; Time</th>
              <th className="text-left p-3">File Size</th>
              <th className="text-left p-3">Source</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {backups.map(b => (
              <tr key={b.id} data-testid={`ob-row-${b.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs">{b.filename}</td>
                <td className="p-3 text-xs">{fmtDT(b.created_at)}</td>
                <td className="p-3 font-mono text-xs" data-testid={`ob-size-${b.id}`}>{fmtSize(b.size)}</td>
                <td className="p-3">
                  <Badge variant={b.trigger === "manual" ? "default" : "secondary"} className="rounded-sm text-[10px] uppercase">{b.trigger}</Badge>
                  {b.email_sent_to && <Mail className="w-3 h-3 inline ml-2 text-muted-foreground" title={`Emailed to ${[].concat(b.email_sent_to).join(", ")}`}/>}
                </td>
                <td className="p-3 text-right">
                  <Button data-testid={`ob-download-${b.id}`} size="sm" variant="ghost" onClick={() => download(b)} className="h-7 w-7 p-0"><Download className="w-3.5 h-3.5"/></Button>
                  <Button data-testid={`ob-delete-${b.id}`} size="sm" variant="ghost" onClick={() => del(b)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3.5 h-3.5"/></Button>
                </td>
              </tr>
            ))}
            {!backups.length && (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground text-xs uppercase tracking-widest" data-testid="ob-empty">
                No backups yet — click Create Backup
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div className="border border-border bg-card rounded-sm p-4" data-testid="ob-auto-section">
          <div className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-2 mb-3"><Clock className="w-3 h-3"/> Auto Backup</div>
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Frequency</Label>
          <Select value={st.frequency} onValueChange={(v) => setSt(s => ({ ...s, frequency: v }))}>
            <SelectTrigger data-testid="ob-freq-select" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
            <SelectContent>
              {FREQS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`ob-freq-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          {st.frequency === "30min" && (
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2" data-testid="ob-retention-note">
              Every 30 Minutes keeps the last 48 backups to avoid storage overflow
            </div>
          )}
        </div>

        <div className="border border-border bg-card rounded-sm p-4" data-testid="ob-email-section">
          <div className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-2 mb-3"><Mail className="w-3 h-3"/> Email Backup</div>
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email Addresses (comma separated)</Label>
          <Input data-testid="ob-emails-input" value={emailText} onChange={(e) => setEmailText(e.target.value)}
            placeholder="owner@shop.com, accounts@shop.com" className="mt-1 rounded-sm bg-secondary border-border"/>
          <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2 mt-3">
            <span className="text-xs">Send Backup by Email after each auto backup</span>
            <Switch data-testid="ob-auto-email-toggle" checked={st.auto_email} onCheckedChange={(v) => setSt(s => ({ ...s, auto_email: v }))}/>
          </div>
          <div className="flex gap-2 mt-3">
            <Button data-testid="ob-save-settings" onClick={() => saveSettings()} disabled={saving} variant="outline"
              className="rounded-sm uppercase tracking-[0.15em] text-[10px]">{saving ? "Saving…" : "Save Settings"}</Button>
            <Button data-testid="ob-send-now" onClick={sendNow} disabled={sending || backups.length === 0}
              className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-[10px]">
              <Mail className="w-3 h-3 mr-1"/> {sending ? "Sending…" : "Send Now"}
            </Button>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2">
            Email includes the backup file plus organisation name, backup date &amp; time and file size
          </div>
        </div>
      </div>
    </div>
  );
}

export function OrgRestorePage() {
  const [file, setFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  const load = () => api.get("/backups/restore-history").then(r => setHistory(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);

  const doRestore = async () => {
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirm", confirmText);
      await api.post("/backups/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Restore complete — your organisation data was replaced from the backup");
      setFile(null); setConfirmText(""); setConfirmOpen(false);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRestoring(false); }
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="org-restore-page">
      <PageHeader eyebrow="Settings · Backup & Restore" title="Restore" testId="org-restore-title"
        sub="Upload a backup file created on the Backup page to replace your current data"/>

      <div className="border border-destructive/40 bg-card rounded-sm p-4 mb-6" data-testid="or-restore-card">
        <div className="flex items-start gap-3 border border-destructive/40 bg-destructive/10 rounded-sm p-3 mb-4" data-testid="or-warning">
          <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0"/>
          <div className="text-sm">
            <strong className="text-destructive">Warning:</strong> restoring <strong>overwrites all current organisation data</strong> —
            transactions, master records, stock, user accounts and settings — with the contents of the backup file.
            This cannot be undone. Create a fresh backup first if unsure.
          </div>
        </div>
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Backup File (.json)</Label>
            <Input ref={fileRef} data-testid="or-file-input" type="file" accept=".json,application/json"
              onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 rounded-sm bg-secondary border-border w-72"/>
          </div>
          <Button data-testid="or-restore-btn" onClick={() => setConfirmOpen(true)} disabled={!file} variant="destructive"
            className="rounded-sm uppercase tracking-[0.15em] text-xs">
            <Undo2 className="w-4 h-4 mr-1"/> Restore
          </Button>
        </div>
        {file && <div className="text-xs text-muted-foreground mt-2" data-testid="or-file-name">Selected: {file.name} ({fmtSize(file.size)})</div>}
      </div>

      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Restore History</div>
      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="or-history-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Backup Used</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {history.map(h => (
              <tr key={h.id} data-testid={`or-row-${h.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 text-xs">{fmtDT(h.created_at)}</td>
                <td className="p-3 font-mono text-xs">{h.backup_name}</td>
                <td className="p-3">
                  <Badge variant={h.status === "Success" ? "default" : "destructive"} className="rounded-sm text-[10px] uppercase"
                    data-testid={`or-status-${h.id}`} title={h.error || ""}>{h.status}</Badge>
                </td>
              </tr>
            ))}
            {!history.length && (
              <tr><td colSpan={3} className="p-8 text-center text-muted-foreground text-xs uppercase tracking-widest" data-testid="or-history-empty">
                No restores performed yet
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(v) => { setConfirmOpen(v); if (!v) setConfirmText(""); }}>
        <DialogContent data-testid="or-confirm-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive"/> Confirm Restore
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            You are about to overwrite <strong>all current organisation data</strong> with
            <strong className="font-mono text-xs"> {file?.name}</strong>. This cannot be undone.
          </p>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Type RESTORE to confirm</Label>
            <Input data-testid="or-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
              placeholder="RESTORE" className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
          </div>
          <div className="flex gap-2">
            <Button data-testid="or-confirm-btn" onClick={doRestore} disabled={restoring || confirmText !== "RESTORE"}
              variant="destructive" className="flex-1 rounded-sm uppercase tracking-[0.15em]">
              {restoring ? "Restoring…" : "Restore Now"}
            </Button>
            <Button data-testid="or-cancel-btn" variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-sm uppercase tracking-[0.15em]">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
